import { Copy, ExternalLink, Send } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  ARTIST_KINDS,
  ARTIST_KIND_LABELS,
  BOOKING_BUDGET_LABELS,
  BOOKING_EVENT_TYPE_LABELS,
  BOOKING_OUTCOME_LABELS,
  PROPOSAL_STATUS_LABELS,
  type ArtistKind,
  type BookingBudget,
  type BookingEventType,
  type BookingOutcome,
  type ProposalStatus,
} from '@sse/schema/booking';
import { comuneByIstat } from '@sse/gazetteer';
import { btnPrimary, btnSecondary, inputCls } from '../components/ui';
import { ApiError } from '../lib/api';
import { formatStamp } from '../lib/format';
import { useSession } from '../lib/session';

/**
 * Artisti per gli organizzatori (redazione, docs/07-admin-e-bot.md "Artisti"): il funnel della
 * prova (richieste, consegne, letture, risposte, esiti), le richieste da mandare a mano su
 * Instagram o Facebook e l'anagrafica degli artisti con i contatti per la consegna.
 */

interface Proposal {
  slug: string;
  name: string;
  channel: 'email' | 'concierge';
  status: ProposalStatus;
  link: string;
  conciergeText?: string;
  /** contatti attuali dell'artista, per girare la richiesta a mano */
  contact?: { email?: string; instagram?: string; facebook?: string; page?: string };
  sentAt?: string;
  sentBy?: string;
  openedAt?: string;
  repliedAt?: string;
  reply?: { email?: string; phone?: string; message?: string; fee?: string; reason?: string };
}
interface Request {
  id: string;
  createdAt: string;
  organizer: { name: string; organization?: string; email: string; phone?: string };
  event: {
    title: string;
    type: BookingEventType;
    comuneId: string;
    dates: string[];
    time?: string;
    budget?: BookingBudget;
  };
  message?: string;
  outcome?: BookingOutcome;
  proposals: Proposal[];
}
interface Funnel {
  requests: number;
  organizers: number;
  proposals: number;
  delivered: number;
  toSend: number;
  opened: number;
  replied: number;
  interested: number;
  noReply: number;
  replyHours: number | null;
  outcomes: Record<BookingOutcome, number>;
}
interface Artist {
  slug: string;
  name: string;
  kind: ArtistKind;
  base: string;
  stats: { total: number };
  contact: { email?: string; instagram?: string; facebook?: string; page?: string };
  links: Record<string, string>;
  review?: boolean;
  reviewNote?: string;
  optOut?: boolean;
  hidden?: boolean;
  paused?: boolean;
  photo?: { path: string; source: string; page?: string };
  photoHidden?: boolean;
  /** creato dagli eventi (lettura AI di chi si esibisce) */
  auto?: boolean;
  checked?: boolean;
}

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '–');
const badge = 'px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.04em]';
const TONE: Partial<Record<ProposalStatus, string>> = {
  to_send: 'bg-danger-soft',
  interested: 'bg-ok-soft',
};

export function BookingPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'artists' ? 'artists' : 'requests';
  return (
    <main className="mx-auto max-w-[1100px] p-4 md:p-6">
      <h1 className="font-display text-h2">Artisti per gli organizzatori</h1>
      <p className="mt-1 max-w-[70ch] text-sm text-muted">
        Chi organizza cerca gli artisti sul sito (/organizers/professionals) e manda una richiesta:
        la redazione la gira all’artista a mano (email, Instagram o Facebook) con il messaggio
        pronto, poi la segna come inviata. Se l’artista è interessato, chi organizza riceve risposta
        e contatti.
      </p>
      <div className="mt-4 flex gap-2" role="tablist">
        {(
          [
            ['requests', 'Richieste'],
            ['artists', 'Anagrafica'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            onClick={() => setParams(k === 'artists' ? { tab: k } : {})}
            className={tab === k ? btnPrimary : btnSecondary}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'requests' ? <Requests focus={params.get('id')} /> : <Artists />}
    </main>
  );
}

function Requests({ focus }: { focus: string | null }) {
  const { api } = useSession();
  const [data, setData] = useState<{ funnel: Funnel; requests: Request[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(
    () =>
      api<{ funnel: Funnel; requests: Request[] }>('/booking')
        .then(setData)
        .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e))),
    [api],
  );
  useEffect(() => void load(), [load]);

  const markSent = async (id: string, slug: string) => {
    await api(`/booking/${id}/proposals/${encodeURIComponent(slug)}/sent`, {
      method: 'POST',
    }).catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)));
    await load();
  };

  if (error)
    return (
      <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
        Errore: {error}
      </p>
    );
  if (!data) return <p className="mt-4 text-sm text-muted">Caricamento…</p>;
  const f = data.funnel;
  const tiles: Array<[string, string, string?]> = [
    ['Richieste', String(f.requests), `${f.organizers} organizzatori`],
    [
      'Artisti contattati',
      String(f.proposals),
      f.toSend ? `${f.toSend} da mandare a mano` : undefined,
    ],
    ['Consegnate', String(f.delivered), pct(f.delivered, f.proposals)],
    ['Lette', String(f.opened), pct(f.opened, f.delivered)],
    [
      'Risposte',
      String(f.replied),
      f.replyHours !== null ? `in ${f.replyHours} ore (mediana)` : pct(f.replied, f.delivered),
    ],
    ['Interessati', String(f.interested), pct(f.interested, f.delivered)],
    [
      'Ingaggi dichiarati',
      String(f.outcomes.hired_here),
      `${f.outcomes.other} altro · ${f.outcomes.cancelled} annullati`,
    ],
  ];
  return (
    <>
      <dl className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {tiles.map(([k, v, sub]) => (
          <div key={k} className="border border-surface-strong p-3">
            <dt className="text-xs uppercase tracking-[0.04em] text-muted">{k}</dt>
            <dd className="font-display text-h3 tabular-nums">{v}</dd>
            {sub && <dd className="text-xs text-muted">{sub}</dd>}
          </div>
        ))}
      </dl>
      {data.requests.length === 0 ? (
        <p className="mt-6 text-sm text-muted">Nessuna richiesta per ora.</p>
      ) : (
        <ul className="mt-6 flex flex-col gap-4">
          {data.requests.map((r) => (
            <li
              key={r.id}
              id={r.id}
              className={`border p-4 ${focus === r.id ? 'border-fg' : 'border-surface-strong'}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-medium">
                  {r.event.title}{' '}
                  <span className="font-normal text-muted">
                    · {BOOKING_EVENT_TYPE_LABELS[r.event.type]} ·{' '}
                    {comuneByIstat(r.event.comuneId)?.name} ·{' '}
                    {r.event.dates.length > 2
                      ? `${r.event.dates[0]} → ${r.event.dates.at(-1)}`
                      : r.event.dates.join(', ')}
                    {r.event.time ? ` · ${r.event.time}` : ''}
                    {r.event.budget ? ` · ${BOOKING_BUDGET_LABELS[r.event.budget]}` : ''}
                  </span>
                </p>
                <span className="text-xs text-muted">{formatStamp(r.createdAt)}</span>
              </div>
              <p className="text-sm">
                {r.organizer.name}
                {r.organizer.organization ? ` (${r.organizer.organization})` : ''} ·{' '}
                {r.organizer.email}
                {r.organizer.phone ? ` · ${r.organizer.phone}` : ''}
                {r.outcome ? (
                  <span className={`${badge} ml-2 bg-ok-soft`}>
                    {BOOKING_OUTCOME_LABELS[r.outcome]}
                  </span>
                ) : null}
              </p>
              {r.message && <p className="mt-1 text-sm italic">«{r.message}»</p>}
              <ul className="mt-3 flex flex-col gap-2">
                {r.proposals.map((p) => (
                  <li key={p.slug} className="bg-surface p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{p.name}</span>
                      <span className={`${badge} ${TONE[p.status] ?? 'bg-surface-strong'}`}>
                        {PROPOSAL_STATUS_LABELS[p.status]}
                      </span>
                      <span className="text-xs text-muted">
                        {p.channel === 'email' ? 'via email' : 'a mano'}
                        {p.sentAt ? ` · consegnata ${formatStamp(p.sentAt)}` : ''}
                        {p.sentBy ? ` da ${p.sentBy}` : ''}
                        {p.openedAt ? ` · letta ${formatStamp(p.openedAt)}` : ''}
                        {p.repliedAt ? ` · risposta ${formatStamp(p.repliedAt)}` : ''}
                      </span>
                      <a
                        href={p.link}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-auto inline-flex items-center gap-1 text-sm underline"
                      >
                        Pagina dell’artista <ExternalLink className="size-3.5" aria-hidden />
                      </a>
                    </div>
                    {p.reply && (p.reply.message || p.reply.reason || p.reply.email) && (
                      <p className="mt-1 text-sm">
                        {p.reply.message || p.reply.reason
                          ? `«${p.reply.message ?? p.reply.reason}» `
                          : ''}
                        {[p.reply.email, p.reply.phone, p.reply.fee].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {p.status === 'to_send' && p.conciergeText && (
                      <div className="mt-2 flex flex-col gap-2">
                        <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                          {p.contact?.email && (
                            <a href={`mailto:${p.contact.email}`} className="underline">
                              {p.contact.email}
                            </a>
                          )}
                          {(['instagram', 'facebook', 'page'] as const).map(
                            (k) =>
                              p.contact?.[k] && (
                                <a
                                  key={k}
                                  href={p.contact[k]}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="underline"
                                >
                                  {k === 'page'
                                    ? 'Pagina contatti'
                                    : k === 'instagram'
                                      ? 'Instagram'
                                      : 'Facebook'}
                                </a>
                              ),
                          )}
                          {!p.contact?.email &&
                            !p.contact?.instagram &&
                            !p.contact?.facebook &&
                            !p.contact?.page && (
                              <span className="text-muted">Contatto da cercare</span>
                            )}
                        </p>
                        <textarea
                          readOnly
                          rows={3}
                          value={p.conciergeText}
                          className={`${inputCls} py-2 text-sm`}
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className={btnSecondary}
                            onClick={() =>
                              void navigator.clipboard.writeText(p.conciergeText!).catch(() => {})
                            }
                          >
                            <Copy className="size-4" aria-hidden /> Copia il messaggio
                          </button>
                          <button
                            type="button"
                            className={btnPrimary}
                            onClick={() => void markSent(r.id, p.slug)}
                          >
                            <Send className="size-4" aria-hidden /> Segna come inviata
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function Artists() {
  const { api } = useSession();
  const [q, setQ] = useState('');
  const [review, setReview] = useState(false);
  // artisti creati dagli eventi, da controllare (nome e tipo)
  const [fresh, setFresh] = useState(false);
  const [toCheck, setToCheck] = useState<number | null>(null);
  const [rows, setRows] = useState<Artist[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const id = setTimeout(() => {
      api<{ artists: Artist[]; toCheck: number }>(
        `/booking/artists?q=${encodeURIComponent(q)}${review ? '&review=1' : ''}${fresh ? '&new=1' : ''}`,
      )
        .then((r) => {
          setRows(r.artists);
          setToCheck(r.toCheck);
        })
        .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)));
    }, 250);
    return () => clearTimeout(id);
  }, [api, q, review, fresh]);
  return (
    <section className="mt-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca un artista"
          className={`${inputCls} max-w-xs`}
        />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={review} onChange={(e) => setReview(e.target.checked)} />{' '}
          Solo da verificare
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={fresh} onChange={(e) => setFresh(e.target.checked)} />{' '}
          Nuovi dagli eventi da controllare{toCheck !== null ? ` (${toCheck})` : ''}
        </label>
      </div>
      {fresh && (
        <p className="mt-2 max-w-[70ch] text-sm text-muted">
          L’AI li ha trovati negli eventi e li ha aggiunti da sola. Controlla nome e tipo (solo
          musica e spettacolo hanno la pagina pubblica), nascondi quelli che non sono artisti, poi
          segna «Controllato».
        </p>
      )}
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {!rows ? (
        <p className="mt-4 text-sm text-muted">Caricamento…</p>
      ) : (
        <ul className="mt-4 flex flex-col divide-y divide-surface-strong border-y border-surface-strong">
          {rows.map((a) => (
            <ArtistRow
              key={a.slug}
              artist={a}
              onSaved={(x) => {
                setRows((rs) => rs!.map((r) => (r.slug === x.slug ? x : r)));
                if (x.checked && a.auto && !a.checked) setToCheck((n) => (n ? n - 1 : n));
              }}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function ArtistRow({ artist: a, onSaved }: { artist: Artist; onSaved: (a: Artist) => void }) {
  const { api, config } = useSession();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: a.name,
    kind: a.kind,
    email: a.contact.email ?? '',
    instagram: a.contact.instagram ?? '',
    facebook: a.contact.facebook ?? '',
    hidden: Boolean(a.hidden),
    photoHidden: Boolean(a.photoHidden),
    review: Boolean(a.review),
  });
  const [msg, setMsg] = useState('');
  const save = async (checked?: boolean) => {
    try {
      const r = await api<Artist>(`/booking/artists/${encodeURIComponent(a.slug)}`, {
        method: 'PATCH',
        body: {
          ...(form.name.trim() !== a.name ? { name: form.name.trim() } : {}),
          ...(form.kind !== a.kind ? { kind: form.kind } : {}),
          ...(checked ? { checked: true } : {}),
          contact: {
            email: form.email.trim(),
            instagram: form.instagram.trim(),
            facebook: form.facebook.trim(),
            ...(a.contact.page ? { page: a.contact.page } : {}),
          },
          hidden: form.hidden,
          photoHidden: form.photoHidden,
          review: form.review,
        },
      });
      onSaved(r);
      setMsg('Salvato');
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    }
  };
  return (
    <li className="py-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-1 text-left hover:bg-surface"
      >
        <span className="min-w-0 flex-1 font-medium">{a.name}</span>
        {a.optOut && <span className={`${badge} bg-danger-soft`}>Non vuole richieste</span>}
        {a.hidden && <span className={`${badge} bg-surface`}>Nascosto</span>}
        {a.paused && <span className={`${badge} bg-surface`}>In attesa</span>}
        {a.review && <span className={`${badge} bg-surface-strong`}>Da verificare</span>}
        {a.auto && !a.checked && (
          <span className={`${badge} bg-accent-soft`}>Nuovo dagli eventi</span>
        )}
        <span className="text-sm text-muted">
          {ARTIST_KIND_LABELS[a.kind]} · {a.base || 'base sconosciuta'} · {a.stats.total} eventi ·{' '}
          {a.contact.email
            ? 'email'
            : a.contact.instagram || a.contact.facebook
              ? 'social'
              : 'nessun contatto'}
        </span>
      </button>
      {open && (
        <div className="mt-2 grid gap-2 bg-surface p-3 sm:grid-cols-3">
          {a.reviewNote && <p className="text-sm sm:col-span-3">Da verificare: {a.reviewNote}</p>}
          <label className="text-sm">
            Nome
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
            />
          </label>
          <label className="text-sm">
            Tipo
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as ArtistKind })}
              className={inputCls}
            >
              {ARTIST_KINDS.map((k) => (
                <option key={k} value={k}>
                  {ARTIST_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          <p className="self-end text-sm">
            <a
              href={`${config.siteUrl.replace(/\/$/, '')}/artists/${a.slug}`}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Pagina sul sito
            </a>
          </p>
          {(['email', 'instagram', 'facebook'] as const).map((k) => (
            <label key={k} className="text-sm">
              {k === 'email'
                ? 'Email per le richieste'
                : k === 'instagram'
                  ? 'Instagram'
                  : 'Facebook'}
              <input
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className={inputCls}
              />
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.hidden}
              onChange={(e) => setForm({ ...form, hidden: e.target.checked })}
            />{' '}
            Nascosto dal sito (anche dalla ricerca per chi organizza)
          </label>
          {a.photo && (
            <div className="flex items-center gap-3 text-sm sm:col-span-3">
              <img
                src={`${config.siteUrl.replace(/\/$/, '')}${a.photo.path}-400.webp`}
                alt=""
                className={`size-16 object-cover ${form.photoHidden ? 'opacity-30' : ''}`}
              />
              <span className="text-muted">
                Foto da{' '}
                {a.photo.page ? (
                  <a href={a.photo.page} target="_blank" rel="noreferrer" className="underline">
                    {a.photo.source}
                  </a>
                ) : (
                  a.photo.source
                )}
              </span>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.photoHidden}
                  onChange={(e) => setForm({ ...form, photoHidden: e.target.checked })}
                />{' '}
                Togli la foto (per esempio se l'artista lo chiede)
              </label>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.review}
              onChange={(e) => setForm({ ...form, review: e.target.checked })}
            />{' '}
            Da verificare
          </label>
          <div className="flex items-center gap-3">
            <button type="button" className={btnPrimary} onClick={() => void save()}>
              Salva
            </button>
            {a.auto && !a.checked && (
              <button type="button" className={btnSecondary} onClick={() => void save(true)}>
                Salva e segna come controllato
              </button>
            )}
            {msg && <span className="text-sm">{msg}</span>}
          </div>
          {Object.keys(a.links).length > 0 && (
            <p className="flex flex-wrap gap-3 text-sm sm:col-span-3">
              {Object.entries(a.links).map(([k, url]) => (
                <a key={k} href={url} target="_blank" rel="noreferrer" className="underline">
                  {k}
                </a>
              ))}
            </p>
          )}
        </div>
      )}
    </li>
  );
}
